import { useState, useEffect, useCallback, useRef } from 'react';
import { API_V1, authFetch, formatAmount, formatAmountCompact } from '../constants';
import StatusBadge from '../components/ui/StatusBadge';
import Modal from '../components/ui/Modal';
import { useDOA } from '../hooks/useDOA';
import { useEntities } from '../hooks/useEntities';
import { useGlobalEntity, withEntityParam } from '../hooks/useGlobalEntity';
import PageLayout from '../components/ui/PageLayout';
import KpiGrid from '../components/ui/KpiGrid';
import TabBar from '../components/ui/TabBar';
import DataTable from '../components/ui/DataTable';
import ActionButton from '../components/ui/ActionButton';
import FilterBar from '../components/ui/FilterBar';

const API = `${API_V1}/expenses`;
const HR_API = `${API_V1}/hr/employees`;

interface Line { id: number; expense_date: string; merchant_name: string; amount: number; currency: string; category: string; description: string; policy_status: string; policy_notes: string; receipt_url: string; }
interface Report { id: number; report_number: string; entity_code?: string | null; currency?: string; employee_id?: number | null; employee_name: string; report_date: string; purpose: string; total_amount: number; reimbursable_amount: number; card_amount: number; policy_violations: number; lifecycle_status: string; version: number; approved_by?: number | null; approved_at?: string | null; lines: Line[]; }
interface Summary { total: number; draft: number; submitted: number; approved: number; posted: number; rejected: number; total_amount: number; total_violations: number; }
interface Employee { id: number; full_name?: string; first_name?: string; last_name?: string; email?: string; department?: string; entity_code?: string | null; }
interface HistoryEntry { id: number; action: string; field_name?: string | null; old_value?: string | null; new_value?: string | null; changed_by: string; changed_at: string; }

const CATEGORIES = ['MEALS', 'HOTEL', 'TRANSPORT', 'AIRFARE', 'OFFICE_SUPPLIES', 'CLIENT_ENTERTAINMENT', 'OTHER'];
const PURPOSE_OPTIONS = [
  'Business Travel',
  'Client Meeting',
  'Conference / Training',
  'Team Building',
  'Office Supplies',
  'Professional Services',
  'Other',
];

interface FormLine { expense_date: string; merchant_name: string; amount: string; currency: string; category: string; description: string; receipt_url: string; receipt_filename: string; }

const emptyLine = (): FormLine => ({
  expense_date: new Date().toISOString().slice(0, 10),
  merchant_name: '',
  amount: '',
  currency: 'USD',
  category: 'MEALS',
  description: '',
  receipt_url: '',
  receipt_filename: '',
});

const CURRENCIES = ['USD', 'SAR', 'BRL', 'EUR', 'GBP', 'AED', 'JPY'];

export default function ExpenseManagement() {
  const { canApprove } = useDOA();
  const entities = useEntities();
  const { entity } = useGlobalEntity();
  const [reports, setReports] = useState<Report[]>([]);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [history, setHistory] = useState<Record<number, HistoryEntry[]>>({});
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);
  const [form, setForm] = useState({
    entity_code: '',
    employee_id: '' as string | number,
    employee_name: '',
    purpose: '',
    lines: [emptyLine()],
  });
  const [tab, setTab] = useState<'reports' | 'reimbursements'>('reports');
  const [reimbData, setReimbData] = useState<any>(null);
  const [overrideTarget, setOverrideTarget] = useState<Report | null>(null);
  const [overrideReason, setOverrideReason] = useState('');
  const [previewReceipt, setPreviewReceipt] = useState<string | null>(null);
  const [matchResult, setMatchResult] = useState<any | null>(null);
  // editingId: null = create mode, number = editing that DRAFT report.
  const [editingId, setEditingId] = useState<number | null>(null);
  // selectedReimb: report_ids checked for individual batch processing.
  const [selectedReimb, setSelectedReimb] = useState<Set<number>>(new Set());
  const lineFileRefs = useRef<Array<HTMLInputElement | null>>([]);
  const PAGE_SIZE = 50;

  const showToast = (msg: string, ok: boolean) => { setToast({ msg, ok }); setTimeout(() => setToast(null), 4000); };

  // FastAPI 422 returns detail as a list of {loc, msg, type} dicts; a
  // plain ``e.detail || 'Error'`` toast renders that as a useless
  // ``[object Object]``. Flatten to readable text so schema mismatches
  // are diagnosable from the UI without opening the network panel.
  const formatErr = (e: any): string => {
    if (!e) return 'Error';
    if (typeof e.detail === 'string') return e.detail;
    if (Array.isArray(e.detail)) {
      return e.detail.map((d: any) => `${(d.loc || []).slice(1).join('.')}: ${d.msg || d.type}`).join('; ') || 'Validation error';
    }
    if (typeof e === 'string') return e;
    try { return JSON.stringify(e).slice(0, 200); } catch { return 'Error'; }
  };

  const load = useCallback(async () => {
    setLoading(true);
    const p = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(page * PAGE_SIZE) });
    if (statusFilter) p.set('lifecycle_status', statusFilter);
    try {
      const [listRes, sumRes] = await Promise.all([
        authFetch(withEntityParam(`${API}?${p}`, entity)),
        authFetch(withEntityParam(`${API}/summary`, entity)),
      ]);
      if (listRes.ok) { const d = await listRes.json(); setReports(d.items || []); setTotal(d.total || 0); }
      if (sumRes.ok) setSummary(await sumRes.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [page, statusFilter, entity]);

  useEffect(() => { load(); }, [load]);

  // Lazy-load HR employees the first time the create-modal opens.
  // Keeps the page render fast and avoids a wasted call when the user
  // never opens the modal.
  useEffect(() => {
    if (showCreate && employees.length === 0) {
      authFetch(HR_API).then(async (res) => {
        if (res.ok) {
          const d = await res.json();
          setEmployees(d.items || d || []);
        }
      }).catch(console.error);
    }
  }, [showCreate, employees.length]);

  // Lazy-load history when a report is expanded.
  const ensureHistory = async (reportId: number) => {
    if (history[reportId]) return;
    try {
      const res = await authFetch(`${API}/${reportId}/history`);
      if (res.ok) {
        const d = await res.json();
        setHistory((h) => ({ ...h, [reportId]: d.history || [] }));
      }
    } catch (e) { console.error(e); }
  };

  const handleEmployeeSelect = (empId: string) => {
    const emp = employees.find((e) => String(e.id) === empId);
    const fullName = emp ? (emp.full_name || `${emp.first_name || ''} ${emp.last_name || ''}`.trim() || emp.email || `Employee ${emp.id}`) : '';
    setForm({ ...form, employee_id: empId ? Number(empId) : '', employee_name: fullName });
  };

  const closeModal = () => {
    setShowCreate(false);
    setEditingId(null);
    setForm({ entity_code: '', employee_id: '', employee_name: '', purpose: '', lines: [emptyLine()] });
  };

  // Open the modal in edit mode, pre-filled from a DRAFT report.
  const handleEdit = (r: Report) => {
    setEditingId(r.id);
    setForm({
      entity_code: r.entity_code || '',
      employee_id: r.employee_id || '',
      employee_name: r.employee_name || '',
      purpose: r.purpose || '',
      lines: (r.lines || []).map((l) => ({
        expense_date: l.expense_date || new Date().toISOString().slice(0, 10),
        merchant_name: l.merchant_name || '',
        amount: l.amount != null ? String(l.amount) : '',
        currency: l.currency || 'USD',
        category: l.category || 'MEALS',
        description: l.description || '',
        receipt_url: l.receipt_url || '',
        receipt_filename: l.receipt_url ? 'attached' : '',
      })),
    });
    if ((r.lines || []).length === 0) {
      setForm((f) => ({ ...f, lines: [emptyLine()] }));
    }
    setShowCreate(true);
  };

  const handleCreate = async () => {
    try {
      const payload = {
        entity_code: form.entity_code,
        // report_number omitted on create -- backend auto-generates
        // EXP-{entity}-{YYYYMM}-{seq}. On edit it's left unchanged.
        employee_id: form.employee_id || null,
        employee_name: form.employee_name,
        purpose: form.purpose,
        lines: form.lines
          .filter((l) => l.merchant_name && parseFloat(l.amount) > 0)
          .map((l) => ({
            expense_date: l.expense_date || null,
            merchant_name: l.merchant_name,
            amount: parseFloat(l.amount) || 0,
            currency: l.currency || 'USD',
            category: l.category,
            description: l.description,
            receipt_url: l.receipt_url || null,
          })),
      };
      // PUT when editing a DRAFT report, POST when creating new.
      const isEdit = editingId != null;
      const url = isEdit ? `${API}/${editingId}` : API;
      const res = await authFetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        showToast(isEdit ? 'Expense report updated' : 'Expense report created', true);
        closeModal();
        load();
      } else {
        const e = await res.json().catch(() => ({}));
        showToast(formatErr(e), false);
      }
    } catch (err) { showToast(formatErr(err), false); }
  };

  // Process a reimbursement batch. report_ids omitted = all approved;
  // supplied = just those (individual processing).
  const handleProcessBatch = async (reportIds?: number[]) => {
    try {
      const body = reportIds && reportIds.length ? { report_ids: reportIds } : {};
      const res = await authFetch(`${API}/reimbursements/batch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        const d = await res.json();
        showToast(d.message || `Batch processed: ${d.reports_processed} report(s)`, true);
        setSelectedReimb(new Set());
        const r2 = await authFetch(`${API}/reimbursements/pending`);
        if (r2.ok) setReimbData(await r2.json());
        load();
      } else {
        const e = await res.json().catch(() => ({}));
        showToast(formatErr(e), false);
      }
    } catch (err) { showToast(formatErr(err), false); }
  };

  const handleAction = async (id: number, action: string, version: number, body: Record<string, any> = {}) => {
    try {
      const res = await authFetch(`${API}/${id}/${action}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ expected_version: version, ...body }),
      });
      if (res.ok) {
        showToast(`Report ${action}`, true);
        // Force-refresh history for this report so the new transition shows up
        setHistory((h) => { const { [id]: _drop, ...rest } = h; return rest; });
        load();
      } else {
        const e = await res.json().catch(() => ({}));
        showToast(formatErr(e), false);
      }
    } catch (err) { showToast(formatErr(err), false); }
  };

  const handleApproveClick = (r: Report) => {
    if (r.policy_violations > 0) {
      // Open override modal -- backend rejects approval without
      // override_reason when violations > 0.
      setOverrideTarget(r);
      setOverrideReason('');
      return;
    }
    handleAction(r.id, 'approve', r.version);
  };

  const handleOverrideSubmit = async () => {
    if (!overrideTarget) return;
    if (!overrideReason.trim()) {
      showToast('Override reason is required', false);
      return;
    }
    await handleAction(overrideTarget.id, 'approve', overrideTarget.version, { override_reason: overrideReason.trim() });
    setOverrideTarget(null);
    setOverrideReason('');
  };

  const handleLineReceiptUpload = async (lineId: number, file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await authFetch(`${API}/lines/${lineId}/receipt`, { method: 'POST', body: fd });
      if (res.ok) {
        const result = await res.json();
        // Surface the OCR match result. MATCH = green toast +
        // refresh. MISMATCH = open the diff modal so the operator
        // sees what the receipt said vs what was claimed; the
        // receipt is already stored and the discrepancy is already
        // logged to audit_log + appended to the line description.
        if (result.match_status === 'MISMATCH') {
          setMatchResult(result);
        } else if (result.match_status === 'MATCH') {
          showToast('Receipt uploaded — matches claim', true);
        } else if (result.match_status === 'OCR_FAILED') {
          showToast('Receipt uploaded (OCR not available)', true);
        } else {
          showToast('Receipt uploaded', true);
        }
        // Force-refresh history so the OCR audit entry shows up
        setHistory((h) => { const { [result.report_id]: _drop, ...rest } = h; return rest; });
        load();
      } else {
        const e = await res.json().catch(() => ({}));
        showToast(formatErr(e), false);
      }
    } catch (err) { showToast(formatErr(err), false); }
  };

  const addLine = () => setForm({ ...form, lines: [...form.lines, emptyLine()] });
  const updateLine = (i: number, field: keyof FormLine, value: string) => {
    const lines = [...form.lines];
    (lines[i] as any)[field] = value;
    setForm({ ...form, lines });
  };
  const handleLineFile = async (i: number, file: File | undefined) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      showToast('File too large (max 5MB)', false);
      return;
    }
    // AI-native flow: send the file to /extract-receipt, get back
    // {merchant, amount, date, category, ...} + the data URL, and
    // auto-fill any blank line fields. The backend handles OCR via
    // Claude Vision; this endpoint does NOT persist (the persist
    // happens at Create time when the line's receipt_url is included
    // in the create payload).
    showToast('Reading receipt with AI…', true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await authFetch(`${API}/extract-receipt`, { method: 'POST', body: fd });
      if (!res.ok) {
        // Fall back to plain attach (no OCR)
        const reader = new FileReader();
        reader.onload = () => {
          updateLine(i, 'receipt_url', String(reader.result || ''));
          updateLine(i, 'receipt_filename', file.name);
        };
        reader.readAsDataURL(file);
        const e = await res.json().catch(() => ({}));
        showToast(`Attached, but extraction failed: ${formatErr(e)}`, false);
        return;
      }
      const d = await res.json();
      // Always store the data URL so create payload carries the receipt
      const lines = [...form.lines];
      const cur = { ...lines[i] };
      cur.receipt_url = d.receipt_url || '';
      cur.receipt_filename = file.name;
      // Auto-fill ONLY the fields the user hasn't filled in. Never
      // clobber explicit user input -- they may have typed something
      // intentional before picking the receipt.
      if (d.extracted) {
        if (!cur.merchant_name && d.merchant_name) cur.merchant_name = d.merchant_name;
        if ((!cur.amount || parseFloat(cur.amount) === 0) && d.amount) cur.amount = String(d.amount);
        if (!cur.expense_date && d.expense_date) cur.expense_date = d.expense_date;
        if ((!cur.category || cur.category === 'MEALS') && d.category) cur.category = d.category;
        // Currency: trust the OCR-extracted currency unconditionally
        // (NOT just when blank). The default 'USD' is a placeholder
        // until the receipt tells us otherwise. Without this, a SAR
        // receipt got booked as USD and overpaid the employee ~4x.
        if (d.currency) cur.currency = String(d.currency).toUpperCase().slice(0, 5);
        const conf = d.confidence != null ? ` (${Math.round(d.confidence * 100)}% confidence)` : '';
        showToast(`AI-filled from receipt${conf}`, true);
      } else {
        showToast(d.note || 'Receipt attached (no OCR)', true);
      }
      lines[i] = cur;
      setForm({ ...form, lines });
    } catch (err) {
      showToast(formatErr(err), false);
    }
  };

  const inputStyle: React.CSSProperties = { padding: '8px 12px', border: '1px solid var(--border-default)', borderRadius: 6, fontSize: 12, width: '100%', background: 'var(--bg-card)', color: 'var(--text-primary)' };
  const policyColor = (s: string) => s === 'COMPLIANT' ? 'var(--positive)' : 'var(--negative)';

  const toggleReimb = (reportId: number) => {
    setSelectedReimb((prev) => {
      const next = new Set(prev);
      if (next.has(reportId)) next.delete(reportId); else next.add(reportId);
      return next;
    });
  };

  const reimbColumns = [
    { key: '_select', label: '', render: (_: any, r: any) => (
      <input
        type="checkbox"
        checked={selectedReimb.has(r.report_id)}
        onChange={() => toggleReimb(r.report_id)}
        style={{ cursor: 'pointer' }}
      />
    )},
    { key: 'report_number', label: 'Report', render: (v: any) => <span style={{ fontWeight: 600, color: 'var(--accent)' }}>{v}</span> },
    { key: 'employee_name', label: 'Employee', render: (v: any) => v || '-' },
    { key: 'reimbursable_amount', label: 'Reimbursable', align: 'right' as const, render: (v: any) => <span style={{ fontWeight: 600, color: 'var(--positive)' }}>{formatAmount(v)}</span> },
    { key: 'total_amount', label: 'Total', align: 'right' as const, render: (v: any) => formatAmount(v) },
    { key: 'card_amount', label: 'Card', align: 'right' as const, render: (v: any) => <span style={{ color: 'var(--text-secondary)' }}>{formatAmount(v)}</span> },
  ];

  const reportColumns = [
    { key: 'report_number', label: 'Report #', render: (v: any) => <span style={{ fontWeight: 600, color: 'var(--accent)' }}>{v}</span> },
    { key: 'employee_name', label: 'Employee', render: (v: any) => v || '-' },
    { key: 'total_amount', label: 'Amount', align: 'right' as const, render: (v: any, r: Report) => <span style={{ fontWeight: 600 }}>{r.currency || 'USD'} {Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span> },
    { key: 'reimbursable_amount', label: 'Reimb.', align: 'right' as const, render: (v: any, r: Report) => <span style={{ color: 'var(--text-secondary)' }}>{r.currency || 'USD'} {Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span> },
    { key: 'policy_violations', label: 'Violations', render: (v: any) => <span style={{ color: v > 0 ? 'var(--negative)' : 'var(--positive)', fontWeight: 600 }}>{v}</span> },
    { key: 'lifecycle_status', label: 'Status', render: (v: any) => <StatusBadge status={v} /> },
    { key: 'actions', label: 'Actions', render: (_: any, r: Report) => (
      <div style={{ display: 'flex', gap: 4 }} onClick={e => e.stopPropagation()}>
        {r.lifecycle_status === 'DRAFT' && <ActionButton label="Edit" onClick={(e) => { e.stopPropagation(); handleEdit(r); }} variant="secondary" size="sm" />}
        {r.lifecycle_status === 'DRAFT' && <ActionButton label="Submit" onClick={(e) => { e.stopPropagation(); handleAction(r.id, 'submit', r.version); }} variant="primary" size="sm" />}
        {r.lifecycle_status === 'SUBMITTED' && (canApprove(r.total_amount || 0, r.entity_code, 'EXPENSE')
          ? <ActionButton
              label={r.policy_violations > 0 ? 'Approve (override)' : 'Approve'}
              onClick={(e) => { e.stopPropagation(); handleApproveClick(r); }}
              variant={r.policy_violations > 0 ? 'danger' : 'success'}
              size="sm"
            />
          : <ActionButton label="Approve" onClick={() => {}} variant="success" size="sm" disabled />
        )}
        {r.lifecycle_status === 'SUBMITTED' && <ActionButton label="Reject" onClick={(e) => { e.stopPropagation(); handleAction(r.id, 'reject', r.version); }} variant="danger" size="sm" />}
      </div>
    )},
  ];

  const lineColumns = [
    { key: 'expense_date', label: 'Date', render: (v: any) => v || '-' },
    { key: 'merchant_name', label: 'Merchant' },
    { key: 'category', label: 'Category' },
    { key: 'amount', label: 'Amount', align: 'right' as const, render: (v: any, l: Line) => <span style={{ fontWeight: 600 }}>{l.currency || 'USD'} {v?.toFixed(2)}</span> },
    { key: 'policy_status', label: 'Policy', render: (v: any, l: Line) => (
      <div>
        <span style={{ color: policyColor(v), fontWeight: 600, fontSize: 10 }}>{v}</span>
        {l.policy_notes && <div style={{ fontSize: 9, color: 'var(--negative)' }}>{l.policy_notes}</div>}
      </div>
    )},
    { key: 'receipt_url', label: 'Receipt', render: (v: any, l: Line) => v
      ? (
        <button
          onClick={(e) => { e.stopPropagation(); setPreviewReceipt(v); }}
          style={{ background: 'none', border: 'none', color: 'var(--positive)', fontSize: 10, fontWeight: 600, cursor: 'pointer', textDecoration: 'underline' }}
        >
          VIEW
        </button>
      )
      : (
        <label style={{ color: 'var(--negative)', fontSize: 10, fontWeight: 600, cursor: 'pointer', textDecoration: 'underline' }}>
          UPLOAD
          <input
            type="file"
            accept="image/*,application/pdf"
            style={{ display: 'none' }}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) handleLineReceiptUpload(l.id, f);
            }}
          />
        </label>
      )
    },
  ];

  const renderHistory = (entries: HistoryEntry[]) => {
    if (!entries || entries.length === 0) return <div style={{ fontSize: 11, color: 'var(--text-hint)', padding: '8px 0' }}>No history yet.</div>;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: '8px 0' }}>
        {entries.map((h) => (
          <div key={h.id} style={{ display: 'flex', gap: 12, fontSize: 11, padding: '4px 8px', background: 'var(--bg-card)', borderRadius: 4, border: '1px solid var(--border-subtle)' }}>
            <span style={{ color: 'var(--text-hint)', minWidth: 140 }}>{new Date(h.changed_at).toLocaleString()}</span>
            <span style={{ fontWeight: 600, color: 'var(--accent)', minWidth: 110 }}>{h.action}{h.field_name ? ` · ${h.field_name}` : ''}</span>
            <span style={{ color: 'var(--text-secondary)', minWidth: 90 }}>by {h.changed_by}</span>
            <span style={{ color: 'var(--text-primary)', flex: 1 }}>
              {h.action === 'STATUS_CHANGE'
                ? `${h.old_value || '-'} → ${h.new_value || '-'}`
                : (h.new_value || '').slice(0, 200)}
            </span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <PageLayout
      title="Expense Management"
      subtitle="Submit, validate, and approve employee expense reports"
      action={{ label: '+ New Report', onClick: () => setShowCreate(true) }}
    >
      {summary && (
        <KpiGrid items={[
          { label: 'Total', value: summary.total },
          { label: 'Draft', value: summary.draft, color: 'var(--text-secondary)' },
          { label: 'Submitted', value: summary.submitted, color: 'var(--warning)' },
          { label: 'Approved', value: summary.approved, color: 'var(--positive)' },
          { label: 'Posted', value: summary.posted, color: 'var(--accent)' },
          { label: 'Rejected', value: summary.rejected, color: 'var(--negative)' },
          { label: 'Total Amount', value: formatAmountCompact(summary.total_amount), color: 'var(--accent)' },
          { label: 'Violations', value: summary.total_violations, color: summary.total_violations > 0 ? 'var(--negative)' : 'var(--positive)' },
        ]} />
      )}

      <TabBar
        tabs={[
          { key: 'reports', label: 'Expense Reports' },
          { key: 'reimbursements', label: 'Reimbursements' },
        ]}
        active={tab}
        onChange={async (t) => {
          setTab(t as any);
          if (t === 'reimbursements') {
            const res = await authFetch(`${API}/reimbursements/pending`);
            if (res.ok) setReimbData(await res.json());
          }
        }}
      />

      {tab === 'reports' && (
        <FilterBar filters={[
          { key: 'status', label: 'Status', type: 'select', value: statusFilter, onChange: (v) => { setStatusFilter(v); setPage(0); }, options: ['DRAFT', 'SUBMITTED', 'APPROVED', 'POSTED', 'REJECTED'].map(s => ({ value: s, label: s })), width: 200 },
        ]} />
      )}

      {tab === 'reimbursements' ? (
        reimbData ? (
          <div>
            <KpiGrid items={[
              { label: 'Pending', value: reimbData.pending_count },
              { label: 'Total Reimbursable', value: formatAmountCompact(reimbData.total_reimbursable), color: 'var(--accent)' },
              { label: 'Selected', value: selectedReimb.size, color: selectedReimb.size > 0 ? 'var(--accent)' : 'var(--text-secondary)' },
            ]} />
            <DataTable
              columns={reimbColumns}
              rows={reimbData.items || []}
              emptyMessage="No pending reimbursements."
              rowKey="report_id"
            />
            {reimbData.items && reimbData.items.length > 0 && (
              <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
                <ActionButton
                  label={`Process Selected (${selectedReimb.size})`}
                  onClick={() => handleProcessBatch(Array.from(selectedReimb))}
                  variant="success"
                  disabled={selectedReimb.size === 0}
                />
                <ActionButton
                  label="Process All Pending"
                  onClick={() => handleProcessBatch()}
                  variant="secondary"
                />
              </div>
            )}
            <div style={{ marginTop: 10, fontSize: 10, color: 'var(--text-hint)' }}>
              Processing creates a PENDING payment run (DR Accrued Liability / CR Cash-in-Transit).
              Treasury executes it under <b>Treasury → Reimbursement Runs</b> to disburse cash.
            </div>
          </div>
        ) : <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-secondary)' }}>Loading...</div>
      ) : (
        <>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, background: 'var(--bg-card)', borderRadius: 8, border: '1px solid var(--border-subtle)', overflow: 'hidden' }}>
            <thead>
              <tr>
                {reportColumns.map(col => (
                  <th key={col.key} style={{ padding: '10px 14px', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-hint)', borderBottom: '2px solid var(--border-subtle)', background: 'var(--bg-card)', textAlign: col.align || 'left' }}>{col.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={reportColumns.length} style={{ padding: 40, textAlign: 'center', color: 'var(--text-hint)', fontSize: 13 }}>Loading...</td></tr>
              ) : reports.length === 0 ? (
                <tr><td colSpan={reportColumns.length} style={{ padding: 40, textAlign: 'center', color: 'var(--text-hint)', fontSize: 13 }}>No expense reports found.</td></tr>
              ) : reports.map(r => (
                <>
                  <tr key={r.id} onClick={() => { const next = expandedId === r.id ? null : r.id; setExpandedId(next); if (next != null) ensureHistory(next); }} style={{ borderBottom: '1px solid var(--border-subtle)', cursor: 'pointer', background: expandedId === r.id ? 'var(--accent-subtle, #f0f4f8)' : 'transparent' }}>
                    {reportColumns.map(col => (
                      <td key={col.key} style={{ padding: '10px 14px', fontSize: 13, color: 'var(--text-primary)', borderBottom: '1px solid var(--border-subtle)', textAlign: col.align || 'left' }}>
                        {col.render ? col.render((r as any)[col.key], r) : (r as any)[col.key] ?? '-'}
                      </td>
                    ))}
                  </tr>
                  {expandedId === r.id && r.lines && (
                    <tr key={`${r.id}-lines`}><td colSpan={reportColumns.length} style={{ padding: '0 14px 12px 32px', background: 'var(--accent-subtle, #f0f4f8)' }}>
                      <div style={{ marginTop: 8 }}>
                        <DataTable
                          columns={lineColumns}
                          rows={r.lines}
                          rowKey="id"
                          emptyMessage="No expense lines."
                        />
                        <div style={{ marginTop: 16, fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', letterSpacing: '0.04em', textTransform: 'uppercase' }}>Approval History</div>
                        {renderHistory(history[r.id] || [])}
                        {(r.approved_by || r.approved_at) && (
                          <div style={{ marginTop: 8, fontSize: 11, color: 'var(--text-secondary)' }}>
                            Approved by user #{r.approved_by}{r.approved_at ? ` on ${new Date(r.approved_at).toLocaleString()}` : ''}
                          </div>
                        )}
                      </div>
                    </td></tr>
                  )}
                </>
              ))}
            </tbody>
          </table>

          {total > PAGE_SIZE && (
            <div style={{ display: 'flex', justifyContent: 'center', gap: 12, marginTop: 16 }}>
              <ActionButton label="PREV" onClick={() => setPage(Math.max(0, page - 1))} variant="secondary" size="sm" disabled={page === 0} />
              <span style={{ fontSize: 11, color: 'var(--text-secondary)', lineHeight: '28px' }}>{page * PAGE_SIZE + 1}-{Math.min((page + 1) * PAGE_SIZE, total)} of {total}</span>
              <ActionButton label="NEXT" onClick={() => setPage(page + 1)} variant="secondary" size="sm" disabled={(page + 1) * PAGE_SIZE >= total} />
            </div>
          )}
        </>
      )}

      <Modal
        open={showCreate}
        onClose={closeModal}
        title={editingId != null ? 'Edit Expense Report' : 'New Expense Report'}
        size="lg"
        footer={<>
          <ActionButton label="Cancel" onClick={closeModal} variant="secondary" />
          <ActionButton label={editingId != null ? 'Save' : 'Create'} onClick={handleCreate} />
        </>}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <select value={form.entity_code} onChange={e => setForm({ ...form, entity_code: e.target.value })} style={{ ...inputStyle, cursor: 'pointer' }}>
            <option value="">Select Legal Entity *</option>
            {entities.map(e => <option key={e.entity_code} value={e.entity_code}>{e.entity_code} — {e.entity_name} ({e.functional_currency})</option>)}
          </select>
          {editingId == null && (
            <div style={{ fontSize: 10, color: 'var(--text-hint)', marginTop: -6 }}>
              Report number is auto-generated as <code>EXP-{form.entity_code || 'X'}-YYYYMM-NNNN</code> after create.
            </div>
          )}
          <div style={{ display: 'flex', gap: 8 }}>
            <select
              value={String(form.employee_id || '')}
              onChange={(e) => handleEmployeeSelect(e.target.value)}
              style={{ ...inputStyle, cursor: 'pointer', flex: 1 }}
            >
              <option value="">Select Employee {employees.length === 0 ? '(loading…)' : '*'}</option>
              {employees.map((emp) => {
                const name = emp.full_name || `${emp.first_name || ''} ${emp.last_name || ''}`.trim() || emp.email || `Employee ${emp.id}`;
                return <option key={emp.id} value={String(emp.id)}>{name}{emp.department ? ` — ${emp.department}` : ''}</option>;
              })}
            </select>
            <select
              value={form.purpose}
              onChange={(e) => setForm({ ...form, purpose: e.target.value })}
              style={{ ...inputStyle, cursor: 'pointer', flex: 1 }}
            >
              <option value="">Select Purpose</option>
              {PURPOSE_OPTIONS.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <div style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: 12 }}>
            <div style={{ fontSize: 11, fontWeight: 700, marginBottom: 8, color: 'var(--text-secondary)' }}>EXPENSE LINES</div>
            {form.lines.map((l, i) => (
              <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12, padding: 8, border: '1px dashed var(--border-subtle)', borderRadius: 6 }}>
                <div style={{ display: 'flex', gap: 6 }}>
                  <input
                    type="date"
                    value={l.expense_date}
                    onChange={(e) => updateLine(i, 'expense_date', e.target.value)}
                    style={{ ...inputStyle, flex: 1 }}
                  />
                  <input placeholder="Merchant *" value={l.merchant_name} onChange={e => updateLine(i, 'merchant_name', e.target.value)} style={{ ...inputStyle, flex: 2 }} />
                  <input placeholder="Amount *" type="number" step="0.01" value={l.amount} onChange={e => updateLine(i, 'amount', e.target.value)} style={{ ...inputStyle, flex: 1 }} />
                  <select value={l.currency} onChange={e => updateLine(i, 'currency', e.target.value)} style={{ ...inputStyle, width: 80, cursor: 'pointer' }}>
                    {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <select value={l.category} onChange={e => updateLine(i, 'category', e.target.value)} style={{ ...inputStyle, flex: 1, cursor: 'pointer' }}>
                    {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <input placeholder="Description" value={l.description} onChange={e => updateLine(i, 'description', e.target.value)} style={{ ...inputStyle, flex: 3 }} />
                  <input
                    ref={(el) => { lineFileRefs.current[i] = el; }}
                    type="file"
                    accept="image/*,application/pdf"
                    onChange={(e) => handleLineFile(i, e.target.files?.[0])}
                    style={{ ...inputStyle, flex: 2, padding: 6 }}
                  />
                </div>
                {l.receipt_filename && (
                  <div style={{ fontSize: 10, color: 'var(--positive)' }}>
                    Receipt attached: {l.receipt_filename}
                  </div>
                )}
              </div>
            ))}
            <button onClick={addLine} style={{ fontSize: 11, color: 'var(--accent)', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>+ Add Line</button>
          </div>
        </div>
      </Modal>

      {/* Override-reason modal: required when approver tries to approve a
          report with policy_violations > 0. */}
      <Modal
        open={overrideTarget !== null}
        onClose={() => { setOverrideTarget(null); setOverrideReason(''); }}
        title="Approve with Policy Override"
        size="md"
        footer={<>
          <ActionButton label="Cancel" onClick={() => { setOverrideTarget(null); setOverrideReason(''); }} variant="secondary" />
          <ActionButton label="Approve with Override" onClick={handleOverrideSubmit} variant="danger" />
        </>}
      >
        {overrideTarget && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ padding: 10, background: 'var(--negative-bg)', color: 'var(--negative)', borderRadius: 6, fontSize: 12 }}>
              Report <b>{overrideTarget.report_number}</b> has <b>{overrideTarget.policy_violations}</b> policy violation(s).
              Approving requires a written override reason that will be permanently recorded in the audit trail.
            </div>
            <textarea
              placeholder="Why are you approving this report despite the violations? (Required, min 10 chars)"
              value={overrideReason}
              onChange={(e) => setOverrideReason(e.target.value)}
              style={{ ...inputStyle, minHeight: 100, resize: 'vertical' }}
            />
            <div style={{ fontSize: 10, color: 'var(--text-hint)' }}>
              Recorded against your user ID in <code>audit_log</code> · entity_type=expense_reports · action=UPDATE · field=violations_overridden.
            </div>
          </div>
        )}
      </Modal>

      {/* OCR mismatch modal: opens when receipt-upload finds the
          extracted (merchant, amount, date) disagrees with the claim.
          The receipt is already stored and the discrepancy is already
          logged to audit_log + appended to line description. This
          modal is purely informational -- the operator can review the
          diff and then go fix the report manually if they want. */}
      <Modal
        open={matchResult !== null}
        onClose={() => setMatchResult(null)}
        title="Receipt vs Claim — Discrepancy Detected"
        size="md"
        footer={<ActionButton label="OK" onClick={() => setMatchResult(null)} variant="secondary" />}
      >
        {matchResult && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ padding: 10, background: 'var(--negative-bg)', color: 'var(--negative)', borderRadius: 6, fontSize: 12 }}>
              The receipt OCR extracted values that don't match the claim.
              The receipt has been attached and the discrepancy is recorded
              in the audit history. Review and update the line if needed.
            </div>
            <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                  <th style={{ textAlign: 'left', padding: 6, fontSize: 11, color: 'var(--text-hint)' }}>FIELD</th>
                  <th style={{ textAlign: 'left', padding: 6, fontSize: 11, color: 'var(--text-hint)' }}>CLAIM</th>
                  <th style={{ textAlign: 'left', padding: 6, fontSize: 11, color: 'var(--text-hint)' }}>RECEIPT (OCR)</th>
                </tr>
              </thead>
              <tbody>
                {(matchResult.mismatches || []).map((m: any, i: number) => (
                  <tr key={i} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                    <td style={{ padding: 6, fontWeight: 600 }}>{m.field}</td>
                    <td style={{ padding: 6, color: 'var(--text-primary)' }}>{String(m.claim)}</td>
                    <td style={{ padding: 6, color: 'var(--negative)', fontWeight: 600 }}>
                      {String(m.receipt)}
                      {m.delta != null && <span style={{ marginLeft: 6, fontSize: 10 }}>(Δ ${Number(m.delta).toFixed(2)})</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {matchResult.extraction?.confidence != null && (
              <div style={{ fontSize: 10, color: 'var(--text-hint)' }}>
                OCR confidence: {(matchResult.extraction.confidence * 100).toFixed(0)}%
                · method: {matchResult.extraction.method}
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Receipt preview modal: data-URL receipts render directly. */}
      <Modal
        open={previewReceipt !== null}
        onClose={() => setPreviewReceipt(null)}
        title="Receipt"
        size="lg"
        footer={<ActionButton label="Close" onClick={() => setPreviewReceipt(null)} variant="secondary" />}
      >
        {previewReceipt && (
          previewReceipt.startsWith('data:application/pdf')
            ? <iframe src={previewReceipt} style={{ width: '100%', height: '60vh', border: 'none' }} title="Receipt PDF" />
            : <img src={previewReceipt} alt="Receipt" style={{ maxWidth: '100%', maxHeight: '70vh', display: 'block', margin: '0 auto' }} />
        )}
      </Modal>

      {toast && <div style={{ position: 'fixed', top: 16, right: 16, zIndex: 2000, padding: '12px 20px', borderRadius: 8, background: toast.ok ? 'var(--positive-bg)' : 'var(--negative-bg)', color: toast.ok ? 'var(--positive)' : 'var(--negative)', fontSize: 13, fontWeight: 600 }}>{toast.msg}</div>}
    </PageLayout>
  );
}
